import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import api from '../../utils/api';
import { toast } from 'sonner';
import { 
  Ticket, 
  AlertTriangle, 
  Download, 
  Package, 
  Calendar,
  Clock,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Monitor,
  PlayCircle,
  StopCircle
} from 'lucide-react';

const ActivityTab = ({ agentId, agent }) => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [appInstalls, setAppInstalls] = useState([]);
  const [remoteSessions, setRemoteSessions] = useState([]);
  const [activeView, setActiveView] = useState('all');

  useEffect(() => {
    fetchActivityData();
  }, [agentId]);

  const fetchActivityData = async () => {
    setLoading(true);
    try {
      // Fetch all tickets and filter for this specific agent
      const ticketsRes = await api.get('/tickets');
      
      if (ticketsRes.data) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        // Filter tickets specifically for this asset/agent
        const recentTickets = ticketsRes.data
          .filter(t => {
            // Only show tickets that are specifically assigned to this asset
            if (t.asset_id !== agentId) return false;
            
            // Filter by date (last 30 days)
            const ticketDate = new Date(t.created_at);
            return ticketDate >= thirtyDaysAgo;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 10);
        
        setTickets(recentTickets);
      }

      // Fetch alerts (from monitors or agent events)
      // This would come from your monitoring system
      // For now, we'll show placeholder structure
      setAlerts([]);

      // Get recent application installs from agent data
      if (agent.system_details?.installed_apps) {
        // Sort by install date if available, otherwise show recent changes
        const recentApps = agent.system_details.installed_apps
          .filter(app => app.install_date)
          .sort((a, b) => new Date(b.install_date) - new Date(a.install_date))
          .slice(0, 15);
        setAppInstalls(recentApps);
      }

      // Fetch remote control session history
      try {
        const sessionsRes = await api.get(`/agents/${agentId}/remote-sessions`);
        if (sessionsRes.data?.sessions) {
          setRemoteSessions(sessionsRes.data.sessions);
        }
      } catch (error) {
        console.error('Failed to fetch remote sessions:', error);
        // Don't show error toast, just log it
      }

    } catch (error) {
      console.error('Failed to fetch activity data:', error);
      toast.error('Failed to load activity data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-700 border-blue-200',
      'in_progress': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'resolved': 'bg-green-100 text-green-700 border-green-200',
      'closed': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-gray-600',
      'medium': 'text-yellow-600',
      'high': 'text-orange-600',
      'critical': 'text-red-600'
    };
    return colors[priority] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const allActivities = [
    ...tickets.map(t => ({ type: 'ticket', data: t, timestamp: t.created_at })),
    ...alerts.map(a => ({ type: 'alert', data: a, timestamp: a.created_at })),
    ...appInstalls.map(app => ({ type: 'app', data: app, timestamp: app.install_date })),
    ...remoteSessions.map(session => ({ type: 'remote', data: session, timestamp: session.started_at }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="space-y-4">
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Calendar className="w-4 h-4" />
            All Activity
          </TabsTrigger>
          <TabsTrigger value="remote" className="gap-2">
            <Monitor className="w-4 h-4" />
            Remote Sessions ({remoteSessions.length})
          </TabsTrigger>
          <TabsTrigger value="tickets" className="gap-2">
            <Ticket className="w-4 h-4" />
            Tickets ({tickets.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alerts ({alerts.length})
          </TabsTrigger>
          <TabsTrigger value="apps" className="gap-2">
            <Package className="w-4 h-4" />
            App Installs ({appInstalls.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3 mt-4">
          {allActivities.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No recent activity</p>
              </CardContent>
            </Card>
          ) : (
            allActivities.map((activity, idx) => (
              <Card key={idx} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  {activity.type === 'ticket' && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Ticket className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{activity.data.title}</h4>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{activity.data.description}</p>
                          </div>
                          <Badge className={getStatusColor(activity.data.status)}>
                            {activity.data.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(activity.data.created_at)}
                          </span>
                          <span className={`flex items-center gap-1 font-medium ${getPriorityColor(activity.data.priority)}`}>
                            <AlertCircle className="w-3 h-3" />
                            {activity.data.priority} priority
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activity.type === 'alert' && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-50 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{activity.data.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{activity.data.message}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(activity.data.created_at)}
                        </div>
                      </div>
                    </div>
                  )}

                  {activity.type === 'app' && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-50 rounded-lg">
                        <Package className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{activity.data.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Version {activity.data.version} • {activity.data.publisher}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Download className="w-3 h-3" />
                          Installed {formatDate(activity.data.install_date)}
                        </div>
                      </div>
                    </div>
                  )}

                  {activity.type === 'remote' && (
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        activity.data.status === 'active' ? 'bg-blue-50' :
                        activity.data.status === 'failed' ? 'bg-red-50' : 'bg-green-50'
                      }`}>
                        <Monitor className={`w-5 h-5 ${
                          activity.data.status === 'active' ? 'text-blue-600' :
                          activity.data.status === 'failed' ? 'text-red-600' : 'text-green-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">
                              Remote Control Session - {activity.data.type}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              by {activity.data.user_name}
                              {activity.data.user_email && ` (${activity.data.user_email})`}
                            </p>
                          </div>
                          <Badge className={
                            activity.data.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                            activity.data.status === 'failed' ? 'bg-red-100 text-red-700 border-red-200' :
                            'bg-green-100 text-green-700 border-green-200'
                          }>
                            {activity.data.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(activity.data.started_at)}
                          </span>
                          {activity.data.duration_seconds && (
                            <span className="flex items-center gap-1">
                              <PlayCircle className="w-3 h-3" />
                              {Math.floor(activity.data.duration_seconds / 60)}m {activity.data.duration_seconds % 60}s
                            </span>
                          )}
                        </div>
                        {activity.data.error_message && (
                          <p className="text-xs text-red-600 mt-2">{activity.data.error_message}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="remote" className="space-y-3 mt-4">
          {remoteSessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Monitor className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No remote control sessions</p>
                <p className="text-sm text-gray-400 mt-2">
                  Remote control session history will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {remoteSessions.map((session, idx) => (
                <Card key={idx} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        session.status === 'active' ? 'bg-blue-50' :
                        session.status === 'failed' ? 'bg-red-50' : 'bg-green-50'
                      }`}>
                        <Monitor className={`w-5 h-5 ${
                          session.status === 'active' ? 'text-blue-600' :
                          session.status === 'failed' ? 'text-red-600' : 'text-green-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{session.type}</h4>
                              <Badge className={
                                session.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                session.status === 'failed' ? 'bg-red-100 text-red-700 border-red-200' :
                                'bg-green-100 text-green-700 border-green-200'
                              }>
                                {session.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Connected by {session.user_name}
                              {session.user_email && <span className="text-xs text-gray-500"> ({session.user_email})</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <PlayCircle className="w-3 h-3" />
                            Started: {formatDate(session.started_at)}
                          </span>
                          {session.ended_at && (
                            <span className="flex items-center gap-1">
                              <StopCircle className="w-3 h-3" />
                              Ended: {formatDate(session.ended_at)}
                            </span>
                          )}
                          {session.duration_seconds && (
                            <span className="flex items-center gap-1 font-medium text-blue-600">
                              <Clock className="w-3 h-3" />
                              Duration: {Math.floor(session.duration_seconds / 60)}m {session.duration_seconds % 60}s
                            </span>
                          )}
                        </div>
                        
                        {session.error_message && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            {session.error_message}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tickets" className="space-y-3 mt-4">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Ticket className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No recent tickets</p>
              </CardContent>
            </Card>
          ) : (
            tickets.map((ticket) => (
              <Card key={ticket.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">{ticket.title}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{ticket.description}</p>
                    </div>
                    <Badge className={getStatusColor(ticket.status)}>
                      {ticket.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(ticket.created_at)}
                    </span>
                    <span className={`flex items-center gap-1 font-medium ${getPriorityColor(ticket.priority)}`}>
                      <AlertCircle className="w-3 h-3" />
                      {ticket.priority} priority
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-3 mt-4">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No recent alerts</p>
              <p className="text-sm text-gray-400 mt-2">
                Alerts from monitoring and security systems will appear here
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="apps" className="space-y-3 mt-4">
          {appInstalls.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No recent application installs</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {appInstalls.map((app, idx) => (
                <Card key={idx} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-50 rounded-lg">
                        <Package className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{app.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Version {app.version}
                        </p>
                        {app.publisher && (
                          <p className="text-xs text-gray-500 mt-1">by {app.publisher}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Download className="w-3 h-3" />
                          Installed {formatDate(app.install_date)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActivityTab;
